import { useEffect, useState, useCallback } from "react";

/**
 * usePwaInstall — abstracts the browser `beforeinstallprompt` handshake and
 * exposes an install() trigger + platform hints for iOS Safari (which does not
 * fire that event and needs manual "Share → Add to Home Screen" instructions).
 *
 * Returns:
 *   canPrompt  — Chrome/Android/desktop with a deferred prompt in hand
 *   isInstalled — running as a standalone PWA (already installed)
 *   isIOS      — Safari on iOS/iPadOS (needs the manual how-to)
 *   install()  — triggers the native prompt; resolves to true/false
 */
export function usePwaInstall() {
  const [deferred, setDeferred] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Detect already-installed (standalone display-mode OR iOS legacy).
    const check = () => {
      const standalone =
        window.matchMedia?.("(display-mode: standalone)")?.matches ||
        window.navigator?.standalone === true;
      setIsInstalled(!!standalone);
    };
    check();

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setIsInstalled(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return false;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    return outcome === "accepted";
  }, [deferred]);

  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);

  return { canPrompt: !!deferred, isInstalled, isIOS, install };
}
